import { Upload } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { importHl7 } from 'api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { VisuallyHiddenInput } from 'routes/ProjectFormLists/FormTemplateTypesTable';
import { ToastSeverity, toastStateSelector } from 'state';
import { GeneralIds } from 'types/types';

export default function Import() {
  const [fileName, setFileName] = useState<string | null>(null);
  const { t } = useTranslation();
  const { projectId } = useParams<GeneralIds>();
  const [fileContents, setFileContents] = useState<string | null>(null);
  const setToast = useSetRecoilState(toastStateSelector);
  const [loading, setLoading] = useState(false);

  const onChoose = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Object.assign({}, e.target.files);
    const file = files[0];
    if (file) {
      setFileName(file.name);
    }

    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const contents = reader.result as string;
      setFileContents(contents);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const onUpload = async () => {
    if (!fileContents) return;

    setLoading(true);

    try {
      await importHl7({
        hl7Text: fileContents,
        projectId: projectId!,
      });
      setToast({
        isOpen: true,
        message: t('import.uploadSuccess'),
        severity: ToastSeverity.success,
      });
    } catch (e) {
      setToast({
        isOpen: true,
        message: t('import.uploadError'),
        severity: ToastSeverity.error,
      });
    }

    setFileName(null);
    setFileContents(null);
    setLoading(false);
  };

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" mb={2} mt={2}>
        {t('import.chooseFile')}
      </Typography>
      <Box display="flex" alignItems="center" flexDirection={'row'} mb={2}>
        <Button variant="contained" component="label" startIcon={<Upload />}>
          {t('import.chooseFile')}
          <VisuallyHiddenInput
            id="patient_hl7_upload_ignore"
            name="patient_hl7_upload_ignore"
            onChange={onChoose}
            type="file"
          />
        </Button>
        <Typography
          ml={2}
          color={theme => theme.palette.primary.main}
          sx={{ textDecoration: 'underline' }}
        >
          {fileName}
        </Typography>
      </Box>
      <Typography variant="h6" mb={2} mt={2}>
        {t('import.settings')}
      </Typography>
      <Box display="flex" alignItems="start" flexDirection={'column'} mb={2}>
        <FormControl required>
          <FormLabel>{t('import.handleNewPatients')}</FormLabel>
          <RadioGroup defaultValue="1">
            {/* importNewPatients importAllNewPatients dontImportNewPatients */}
            <FormControlLabel
              value="1"
              control={<Radio />}
              label={t('import.handleNewPatientsOptions.importNewPatients')}
            />
            <FormControlLabel
              value="2"
              control={<Radio />}
              label={t('import.handleNewPatientsOptions.importAllNewPatients')}
            />
            <FormControlLabel
              value="3"
              control={<Radio />}
              label={t('import.handleNewPatientsOptions.dontImportNewPatients')}
            />
          </RadioGroup>
        </FormControl>
      </Box>
      <Box mt={2}>
        <FormControl required>
          <FormLabel>{t('import.statusOfForm')}</FormLabel>
          <RadioGroup
            defaultValue="1"
            sx={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <FormControlLabel
              value="1"
              control={<Radio />}
              label={t('forms.status.filling')}
            />
            <FormControlLabel
              value="2"
              control={<Radio />}
              label={t('forms.status.finalized')}
            />
          </RadioGroup>
        </FormControl>
      </Box>
      <Box display="flex" alignItems="center" justifyContent={'flex-end'}>
        <Button
          variant="contained"
          onClick={onUpload}
          disabled={!fileContents || loading}
        >
          {loading && (
            <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
          )}
          {t('import.upload')}
        </Button>
      </Box>
    </Paper>
  );
}
